import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import storage from '../firebaseconfig';

/**
 * Upload a certificate image blob to Firebase Storage
 * @param {Blob} imageBlob - The certificate image blob
 * @param {string} certId - Certificate ID (e.g., "ALG-2026-00001")
 * @param {string} studentName - Student name (slugified for filename)
 * @returns {Promise<string>} Download URL of the uploaded certificate
 */
export async function uploadCertificate(imageBlob, certId, studentName) {
  // Create path: certificates/2026/ALG-2026-00001_john-doe.png
  const year = new Date().getFullYear();
  const fileName = `${certId}_${studentName}.png`;
  const storagePath = `certificates/${year}/${fileName}`;

  // Create a reference
  const fileRef = ref(storage, storagePath);

  // Upload the blob
  await uploadBytes(fileRef, imageBlob, {
    contentType: 'image/png',
    customMetadata: {
      certId,
      studentName,
      uploadedAt: new Date().toISOString(),
    },
  });

  // Get download URL
  return getDownloadURL(fileRef);
}

/**
 * Fetch certificate image from Firebase Storage URL
 * @param {string} downloadURL - The Firebase Storage download URL
 * @returns {Promise<Blob>} Certificate image blob
 */
export async function fetchCertificateImage(downloadURL) {
  const response = await fetch(downloadURL);
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.blob();
}

/**
 * Delete a certificate from Firebase Storage
 * @param {string} certId - Certificate ID
 * @param {string} studentName - Student name (slugified)
 * @returns {Promise<void>}
 */
export async function deleteCertificate(certId, studentName) {
  const year = new Date().getFullYear();
  const fileName = `${certId}_${studentName}.png`;
  const storagePath = `certificates/${year}/${fileName}`;

  const fileRef = ref(storage, storagePath);
  await deleteObject(fileRef);
}

/**
 * Build a cert payload that includes the Firebase storage URL
 * Embeds download URL in the QR code data
 * @param {object} certData - { certId, name, issuedAt, downloadURL }
 * @returns {string} Base64 encoded JSON payload
 */
export function buildCertPayloadWithStorage(certData) {
  const payload = {
    certId: certData.certId,
    name: certData.name,
    issuedAt: certData.issuedAt,
    imageUrl: certData.downloadURL, // Firebase Storage URL
  };
  return btoa(encodeURIComponent(JSON.stringify(payload)));
}

/**
 * Extract cert payload from base64 string
 * @param {string} encodedPayload - Base64 encoded payload
 * @returns {object} Decoded payload with imageUrl
 */
export function decodeCertPayloadWithStorage(encodedPayload) {
  try {
    const decoded = decodeURIComponent(atob(encodedPayload));
    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
}
